// The "New" view (2026-10-08): any agent composes and sends a brand-new email
// from service@1aauto.com or service@am-autoparts.com, outside any contact.
// The send (POST /api/m365/new-email) creates a contact that is closed from
// the start; the customer's reply reopens it like any other contact.
//
// Built from the reply box's own parts: RichReplyEditor, the CC field's
// parsing (emailCc.js), the attachment list and size cap
// (EmailAttachmentList.jsx, emailAttachments.js), the template picker
// (TemplatePickerDialog.jsx) and its email insert path (templateInsert.js).
// The rules live in newEmail.js. Nothing is saved as a draft.
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, Link, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material'
import RichReplyEditor from './RichReplyEditor.jsx'
import TemplatePickerDialog from './TemplatePickerDialog.jsx'
import EmailAttachmentList from './EmailAttachmentList.jsx'
import { fetchNewEmailOptions, sendNewEmail } from './api.js'
import { readEmailAttachmentFile } from './emailAttachments.js'
import { parseCcText } from './emailCc.js'
import { emailTemplateInsertHtml, insertHtmlTemplate } from './templateInsert.js'
import { unfilledPlaceholderWarning } from './placeholderCheck.js'
import { SELECT_FROM_MESSAGE, newEmailTemplateScope, resolvedSignature, swapSignature, newEmailSendState, isNewEmailDirty } from './newEmail.js'

const fieldSx = { '& .MuiInputBase-input': { py: '6px', fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.7rem', mx: 0 } }

export default function NewEmailView({ firstName = '', onDirtyChange }) {
  const [options, setOptions] = useState([])
  const [optionsError, setOptionsError] = useState(null)
  const [fromAccount, setFromAccount] = useState('')
  const [to, setTo] = useState('')
  const [ccText, setCcText] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState([])
  const [attachmentError, setAttachmentError] = useState(null)
  const [templateOpen, setTemplateOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [sent, setSent] = useState(null)
  const editorRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    let active = true
    fetchNewEmailOptions()
      .then((list) => { if (active) setOptions(list) })
      .catch((err) => { if (active) setOptionsError(`Could not load the From addresses: ${err.message}`) })
    return () => { active = false }
  }, [])

  const option = options.find((o) => o.account === fromAccount) ?? null
  const signature = resolvedSignature(option?.signatureTemplate, firstName)
  const sendState = newEmailSendState({ from: fromAccount, to, ccText, subject, body, signature })
  const ccInvalid = useMemo(() => parseCcText(ccText).invalid, [ccText])
  const placeholderWarning = useMemo(() => unfilledPlaceholderWarning(body), [body])
  const dirty = isNewEmailDirty({ to, ccText, subject, body, attachments, signature })
  useEffect(() => { onDirtyChange?.(dirty) }, [dirty, onDirtyChange])

  // Changing From keeps the text and swaps the signature.
  function changeFrom(account) {
    const next = options.find((o) => o.account === account) ?? null
    setBody((prev) => swapSignature(prev, signature, resolvedSignature(next?.signatureTemplate, firstName)))
    setFromAccount(account)
  }

  function clearForm() {
    setFromAccount('')
    setTo('')
    setCcText('')
    setSubject('')
    setBody('')
    setAttachments([])
    setAttachmentError(null)
    setSendError(null)
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setAttachmentError(null)
    const { attachment, error } = await readEmailAttachmentFile(file, { attachments, bodyHtml: body })
    if (error) { setAttachmentError(error); return }
    setAttachments((prev) => [...prev, attachment])
  }

  function insertTemplate(template) {
    const html = emailTemplateInsertHtml(template.content, { account: fromAccount, firstName })
    if (!editorRef.current?.insertTemplate(html, [signature, option?.signatureTemplate].filter(Boolean))) {
      setBody((prev) => insertHtmlTemplate(prev, html, [signature]))
    }
    setTemplateOpen(false)
  }

  async function send() {
    if (!sendState.canSend || busy) return
    setBusy(true)
    setSendError(null)
    setSent(null)
    try {
      const result = await sendNewEmail({
        fromAccount, to: to.trim(), cc: sendState.ccAddresses, subject: subject.trim(), bodyHtml: body,
        attachments: attachments.map((a) => ({ name: a.name, dataUrl: a.dataUrl })),
      })
      clearForm()
      setSent(result)
    } catch (err) {
      setSendError(err.body?.message ?? err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }} data-testid="new-email-view">
      <Stack spacing={1.25} sx={{ maxWidth: 960 }}>
        <Typography variant="h6">New email</Typography>
        {optionsError && <Alert severity="error">{optionsError}</Alert>}
        {sent && (
          <Alert severity="success" data-testid="new-email-sent" onClose={() => setSent(null)}>
            Sent.{' '}
            {/* Opens the new (closed) contact in its own tab, so the New view stays here.
                Hidden when the server says the sender can't view it (a csr
                without Support Email, sending from a Customer Support mailbox,
                2026-10-12), so there is no dead link. */}
            {sent.canOpen !== false && (
              <Link href={`/contact/${sent.convNumber}`} target="_blank" rel="noopener" data-testid="new-email-open-contact">Open contact</Link>
            )}
          </Alert>
        )}
        {sendError && <Alert severity="error" onClose={() => setSendError(null)} data-testid="new-email-error">{sendError}</Alert>}

        <FormControl size="small" fullWidth required>
          <InputLabel id="new-email-from-label" sx={{ fontSize: '0.8rem' }}>From</InputLabel>
          <Select labelId="new-email-from-label" label="From" value={fromAccount} onChange={(e) => changeFrom(e.target.value)} disabled={busy}
            data-testid="new-email-from" sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: '6px' } }}>
            {/* The From descriptor (2026-10-12), from the server's options; the
                address the customer will see stays visible beside it. */}
            {options.map((o) => (
              <MenuItem key={o.account} value={o.account} data-testid={`new-email-from-option-${o.account}`} title={o.address}>
                {o.label ?? o.address}
                {o.label && o.label !== o.address && (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{o.address}</Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="To" required value={to} onChange={(e) => setTo(e.target.value)} size="small" fullWidth disabled={busy}
          inputProps={{ 'data-testid': 'new-email-to' }} sx={fieldSx} />
        <TextField label="CC" value={ccText} onChange={(e) => setCcText(e.target.value)} size="small" fullWidth disabled={busy}
          error={ccInvalid.length > 0} helperText={ccInvalid.length ? `Not an email address: ${ccInvalid.join(', ')}` : null}
          inputProps={{ 'data-testid': 'new-email-cc' }} sx={fieldSx} />
        <TextField label="Subject" required value={subject} onChange={(e) => setSubject(e.target.value)} size="small" fullWidth disabled={busy}
          inputProps={{ 'data-testid': 'new-email-subject' }} sx={fieldSx} />

        <Stack spacing={0.5}>
          <input ref={fileInputRef} type="file" hidden aria-label="Add attachment" onChange={handleFileSelected} />
          {attachmentError && <Alert severity="error" onClose={() => setAttachmentError(null)}>{attachmentError}</Alert>}
          <EmailAttachmentList attachments={attachments} onRemove={(id) => setAttachments((prev) => prev.filter((a) => a.id !== id))} disabled={busy} />
        </Stack>

        <RichReplyEditor isCustomerReply ref={editorRef} label="Body" value={body} minRows={8} onChange={setBody} disabled={busy}
          onAddAttachment={() => fileInputRef.current?.click()}
          onOpenTemplates={() => { editorRef.current?.captureSelection(); setTemplateOpen(true) }} />
        {placeholderWarning && <Alert severity="warning">{placeholderWarning}</Alert>}

        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" onClick={send} disabled={!sendState.canSend || busy} data-testid="new-email-send"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
          <Button onClick={clearForm} disabled={busy} data-testid="new-email-discard">Discard</Button>
          {!sendState.canSend && dirty && (
            <Typography variant="caption" color="text.secondary" data-testid="new-email-send-reason">{sendState.reason}</Typography>
          )}
        </Stack>
      </Stack>

      <TemplatePickerDialog
        open={templateOpen}
        onClose={() => setTemplateOpen(false)}
        onUse={insertTemplate}
        scope={newEmailTemplateScope(option)}
        showOtherClients={false}
        noScopeMessage={SELECT_FROM_MESSAGE}
      />
    </Box>
  )
}
